import { useState } from "react";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Badge } from "@project/components/ui/badge";
import { useI18n } from "@project/lib/use-i18n";
import {
  UtensilsCrossed,
  HeartHandshake,
  Users,
  Heart,
  ArrowRight,
  Check,
  Clock,
  CalendarCheck,
  Leaf,
} from "lucide-react";
import mealImg from "@project/assets/home-meal.webp";
import {
  TiffinSevaInquiryModal,
  type AgeCategory,
} from "@project/user/components/tiffin-seva-inquiry-modal";
import { TiffinSevaDonationModal } from "@project/user/components/tiffin-seva-donation-modal";

// 4 Specialization Highlights
const SPECIALIZATIONS = [
  {
    step: "01",
    titleKey: "programs.tiffinSeva.specialization.items.jainFood.title",
    taglineKey: "programs.tiffinSeva.specialization.items.jainFood.tagline",
    descKey: "programs.tiffinSeva.specialization.items.jainFood.desc",
    icon: Leaf,
    badgeKey: "programs.tiffinSeva.specialization.items.jainFood.badge",
    accentBg: "from-emerald-500/15 to-emerald-500/5",
    iconBg: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    borderHover: "hover:border-emerald-500/50",
  },
  {
    step: "02",
    titleKey: "programs.tiffinSeva.specialization.items.delivery.title",
    taglineKey: "programs.tiffinSeva.specialization.items.delivery.tagline",
    descKey: "programs.tiffinSeva.specialization.items.delivery.desc",
    icon: Clock,
    badgeKey: "programs.tiffinSeva.specialization.items.delivery.badge",
    accentBg: "from-amber-500/15 to-amber-500/5",
    iconBg: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    borderHover: "hover:border-amber-500/50",
  },
  {
    step: "03",
    titleKey: "programs.tiffinSeva.specialization.items.regular.title",
    taglineKey: "programs.tiffinSeva.specialization.items.regular.tagline",
    descKey: "programs.tiffinSeva.specialization.items.regular.desc",
    icon: CalendarCheck,
    badgeKey: "programs.tiffinSeva.specialization.items.regular.badge",
    accentBg: "from-blue-500/15 to-blue-500/5",
    iconBg: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
    borderHover: "hover:border-blue-500/50",
  },
  {
    step: "04",
    titleKey: "programs.tiffinSeva.specialization.items.authentic.title",
    taglineKey: "programs.tiffinSeva.specialization.items.authentic.tagline",
    descKey: "programs.tiffinSeva.specialization.items.authentic.desc",
    icon: UtensilsCrossed,
    badgeKey: "programs.tiffinSeva.specialization.items.authentic.badge",
    accentBg: "from-rose-500/15 to-rose-500/5",
    iconBg: "bg-rose-500/10 text-rose-700 dark:text-rose-400",
    borderHover: "hover:border-rose-500/50",
  },
];

// Eligibility Cards
const ELIGIBILITY_CARDS = [
  {
    titleKey: "programs.tiffinSeva.eligibility.cards.senior.title",
    descKey: "programs.tiffinSeva.eligibility.cards.senior.desc",
    icon: Users,
    tagKey: "programs.tiffinSeva.eligibility.cards.senior.tag",
    category: "above_60" as AgeCategory,
  },
  {
    titleKey: "programs.tiffinSeva.eligibility.cards.general.title",
    descKey: "programs.tiffinSeva.eligibility.cards.general.desc",
    icon: HeartHandshake,
    tagKey: "programs.tiffinSeva.eligibility.cards.general.tag",
    category: "below_60" as AgeCategory,
  },
  {
    titleKey: "programs.tiffinSeva.eligibility.cards.divyang.title",
    descKey: "programs.tiffinSeva.eligibility.cards.divyang.desc",
    icon: Heart,
    tagKey: "programs.tiffinSeva.eligibility.cards.divyang.tag",
    category: "below_60" as AgeCategory,
  },
];

// How It Works Steps
const STEPS = [
  { step: "01", titleKey: "programs.tiffinSeva.howItWorks.steps.step1.title", descKey: "programs.tiffinSeva.howItWorks.steps.step1.desc" },
  { step: "02", titleKey: "programs.tiffinSeva.howItWorks.steps.step2.title", descKey: "programs.tiffinSeva.howItWorks.steps.step2.desc" },
  { step: "03", titleKey: "programs.tiffinSeva.howItWorks.steps.step3.title", descKey: "programs.tiffinSeva.howItWorks.steps.step3.desc" },
  { step: "04", titleKey: "programs.tiffinSeva.howItWorks.steps.step4.title", descKey: "programs.tiffinSeva.howItWorks.steps.step4.desc" },
];

export default function TiffinPage() {
  const { t } = useI18n();
  // Modal states
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<AgeCategory | undefined>(undefined);
  const [donationModalOpen, setDonationModalOpen] = useState(false);

  const handleOpenModal = (cat?: AgeCategory) => {
    setSelectedCategory(cat);
    setInquiryModalOpen(true);
  };

  return (
    <PageShell>
      {/* Hero Banner */}
      <PageHero
        eyebrow={t("programs.tiffinSeva.hero.eyebrow")}
        title={t("programs.tiffinSeva.hero.title")}
        subtitle={t("programs.tiffinSeva.hero.subtitle")}
        image={mealImg}
      />

      {/* Main Content Area */}
      <section className="py-10 px-4 max-w-7xl mx-auto space-y-14">
        {/* About Tiffin Seva */}
        <div className="rounded-3xl border border-border bg-card p-8 md:p-10 shadow-xs">
          <div className="grid gap-8 md:grid-cols-2 md:divide-x md:divide-border items-center">
            {/* Left Column */}
            <div className="space-y-2 md:pr-8">
              <span className="text-xs font-bold text-primary tracking-widest uppercase">
                {t("programs.tiffinSeva.about.eyebrow")}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
                {t("programs.tiffinSeva.about.title")}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {t("programs.tiffinSeva.about.tagline")}
              </p>
            </div>

            {/* Right Column */}
            <div className="space-y-4 md:pl-8">
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {t("programs.tiffinSeva.about.body")}
              </p>

              {/* Action Buttons: Apply for Tiffin Seva & Support and Donate */}
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <Button
                  size="sm"
                  onClick={() => handleOpenModal()}
                  className="bg-primary text-primary-foreground font-semibold text-xs gap-1.5 shadow-sm"
                >
                  <UtensilsCrossed className="h-3.5 w-3.5" /> {t("programs.tiffinSeva.about.applyBtn")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDonationModalOpen(true)}
                  className="border-primary/40 text-primary hover:bg-primary/10 font-semibold text-xs gap-1.5 shadow-xs transition-all"
                >
                  <HeartHandshake className="h-3.5 w-3.5 text-rose-500" /> {t("programs.tiffinSeva.about.donateBtn")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* TIFFIN SEVA SPECIALIZATION SECTION (4 POINTS WITH INTERACTIVE ICONS) */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-1.5">
            <Badge variant="secondary" className="bg-primary/10 text-primary font-bold text-xs px-3 py-1">
              {t("programs.tiffinSeva.specialization.badge")}
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight font-serif">
              {t("programs.tiffinSeva.specialization.title")}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {t("programs.tiffinSeva.specialization.desc")}
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SPECIALIZATIONS.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.titleKey}
                  className={`group relative rounded-3xl border border-border bg-gradient-to-b ${item.accentBg} p-6 shadow-xs transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg ${item.borderHover} cursor-default`}
                >
                  {/* Top Bar with Icon & Badge */}
                  <div className="flex items-center justify-between">
                    <div
                      className={`flex h-13 w-13 items-center justify-center rounded-2xl ${item.iconBg} shadow-xs transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <Badge variant="outline" className="font-bold text-[10px] bg-background/80 backdrop-blur-xs">
                      {t(item.badgeKey)}
                    </Badge>
                  </div>

                  {/* Title & Tagline */}
                  <div className="mt-5 space-y-1">
                    <span className="text-[11px] font-bold text-primary tracking-wider uppercase">
                      {item.step}. {t(item.taglineKey)}
                    </span>
                    <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                      {t(item.titleKey)}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="mt-2.5 text-xs text-muted-foreground leading-relaxed">
                    {t(item.descKey)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>


        {/* Eligibility Section */}
        <div className="space-y-8 pt-4">
          <div className="text-center max-w-2xl mx-auto">
            <Badge variant="secondary" className="bg-primary/10 text-primary font-bold text-xs px-3 py-1">
              {t("programs.tiffinSeva.eligibility.badge")}
            </Badge>
            <h2 className="mt-1.5 text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight font-serif">
              {t("programs.tiffinSeva.eligibility.title")}
            </h2>
          </div>

          <div className="grid gap-8 sm:grid-cols-3 max-w-6xl mx-auto">
            {ELIGIBILITY_CARDS.map((c, idx) => {
              const Icon = c.icon;
              return (
                <div key={idx} className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
                      <Icon className="h-6 w-6" />
                    </div>
                    <Badge variant="outline" className="text-[10px] font-bold">
                      {t(c.tagKey)}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-bold text-foreground font-serif tracking-tight">{t(c.titleKey)}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t(c.descKey)}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* How It Works */}
        <div className="space-y-6 pt-6">
          <div className="text-center max-w-2xl mx-auto">
            <Badge variant="secondary" className="bg-primary/10 text-primary font-bold">
              {t("programs.tiffinSeva.howItWorks.badge")}
            </Badge>
            <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-foreground">
              {t("programs.tiffinSeva.howItWorks.title")}
            </h2>
          </div>

          <div className="flex flex-nowrap items-center justify-start lg:justify-center overflow-x-auto py-4 px-2 scrollbar-none max-w-full">
            {STEPS.map((s, idx) => (
              <div key={idx} className="flex items-center shrink-0">
                <div className="rounded-2xl border border-border bg-card px-3.5 py-3 sm:px-5 sm:py-3.5 flex items-center gap-2 sm:gap-2.5 shadow-xs transition-all hover:border-primary/50">
                  <span className="flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white text-xs sm:text-sm font-extrabold shadow-xs">
                    {idx + 1}
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-foreground whitespace-nowrap">{t(s.titleKey)}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-primary/60 shrink-0 mx-1.5 sm:mx-2" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 2 Registration Types Showcase */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Card 1: Above 60 Years */}
          <div className="rounded-3xl border-2 border-emerald-500/20 bg-emerald-50/40 dark:bg-emerald-950/10 p-6 sm:p-8 space-y-4 shadow-xs relative overflow-hidden">
            <div className="flex items-center justify-between">
              <Badge className="bg-emerald-600 text-white font-bold text-xs px-3 py-1">
                {t("programs.tiffinSeva.registration.senior.badge")}
              </Badge>
              <span className="text-xs font-bold text-emerald-700">
                {t("programs.tiffinSeva.registration.senior.tag")}
              </span>
            </div>
            <div>
              <h3 className="font-serif text-2xl font-bold text-foreground">
                {t("programs.tiffinSeva.registration.senior.title")}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t("programs.tiffinSeva.registration.senior.desc")}
              </p>
            </div>
            <ul className="space-y-2 text-xs text-foreground/80">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>{t("programs.tiffinSeva.registration.senior.bullet1")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>{t("programs.tiffinSeva.registration.senior.bullet2")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>{t("programs.tiffinSeva.registration.senior.bullet3")}</span>
              </li>
            </ul>
            <Button
              onClick={() => handleOpenModal("above_60")}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs gap-1.5"
            >
              <Users className="h-4 w-4" /> {t("programs.tiffinSeva.registration.senior.btn")}
            </Button>
          </div>

          {/* Card 2: Below 60 Years */}
          <div className="rounded-3xl border-2 border-primary/20 bg-primary/[0.03] dark:bg-primary/[0.08] p-6 sm:p-8 space-y-4 shadow-xs relative overflow-hidden">
            <div className="flex items-center justify-between">
              <Badge className="bg-primary text-primary-foreground font-extrabold text-xs px-3 py-1">
                {t("programs.tiffinSeva.registration.general.badge")}
              </Badge>
              <span className="text-xs font-black text-primary">
                {t("programs.tiffinSeva.registration.general.tag")}
              </span>
            </div>
            <div>
              <h3 className="font-serif text-2xl font-bold text-foreground">
                {t("programs.tiffinSeva.registration.general.title")}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t("programs.tiffinSeva.registration.general.desc")}
              </p>
            </div>
            <ul className="space-y-2 text-xs text-foreground/80">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary shrink-0" />
                <span>{t("programs.tiffinSeva.registration.general.bullet1")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary shrink-0" />
                <span>{t("programs.tiffinSeva.registration.general.bullet2")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary shrink-0" />
                <span>{t("programs.tiffinSeva.registration.general.bullet3")}</span>
              </li>
            </ul>
            <Button
              onClick={() => handleOpenModal("below_60")}
              className="w-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs gap-1.5"
            >
              <HeartHandshake className="h-4 w-4" /> {t("programs.tiffinSeva.registration.general.btn")}
            </Button>
          </div>
        </div>

      </section>

      {/* Tiffin Seva Registration Modal */}
      <TiffinSevaInquiryModal
        open={inquiryModalOpen}
        onOpenChange={setInquiryModalOpen}
        defaultCategory={selectedCategory}
      />

      {/* Tiffin Seva Donation Modal */}
      <TiffinSevaDonationModal
        open={donationModalOpen}
        onOpenChange={setDonationModalOpen}
      />
    </PageShell>
  );
}
